import { createReducer } from '@reduxjs/toolkit';
import { WritableDraft } from 'immer/dist/internal';
import { Action, Reducer } from 'redux';

import {
  AblyStatus,
  ConnectionStatus,
  isStatusConnected,
} from 'pages/views/devices/socketClient/types';
import { Printer } from 'pages/views/devices/types';
import { getHubSmartState } from 'pages/views/devices/utils/storage';

interface ActionWithPayload<Type, Payload> extends Action<Type> {
  payload: Payload;
}

type ActionCreator<Type, Payload> = (payload: Payload) => ActionWithPayload<Type, Payload>;

export interface HubState {
  connection: {
    ably: AblyStatus;
    hub: ConnectionStatus;
    browserCFD: ConnectionStatus;
    local: ConnectionStatus;
  };
  defaultPrinters: {
    RECEIPT?: Printer;
    LABEL?: Printer;
  };
  selectedDisplay:
    | {
        id?: number;
      }
    | Record<string, never>;
  version: string | null;
  hasStartedHubDownload: boolean;
  cfdModal: string;
}

export const initialState: HubState = {
  connection: {
    ably: 'Disconnected',
    hub: 'Disconnected',
    browserCFD: 'Disconnected',
    local: 'Disconnected',
  },
  defaultPrinters: {},
  selectedDisplay: {},
  version: null,
  hasStartedHubDownload: false,
  cfdModal: '',
};

type HUB_ACTIONS =
  | 'ABLY_UPDATE_CONNECTION_STATUS'
  | 'HUB_UPDATE_CONNECTION_STATUS'
  | 'HUB_UPDATE_BROWSER_CFD_CONNECTION_STATUS'
  | 'HUB_UPDATE_VERSION'
  | 'HUB_UPDATE_DEVICES'
  | 'HUB_UPDATE_DEFAULT_PRINTERS'
  | 'HUB_CLEAR_DEVICES'
  | 'HUB_UPDATE_CFD_MODAL'
  | 'HUB_UPDATE_DOWNLOAD_STATUS'
  | 'HUB_PRINT_TIMEOUT'
  | 'HUB_CLEAR_STORE';

export const updateAblyConnectionStatus = (payload: AblyStatus) => ({
  type: 'ABLY_UPDATE_CONNECTION_STATUS',
  payload,
});
export const updateHubConnectionStatus = (payload: ConnectionStatus) => ({
  type: 'HUB_UPDATE_CONNECTION_STATUS',
  payload,
});
export const updateLocalServerConnectionStatus = (payload: ConnectionStatus) => ({
  type: 'HUB_LOCAL_SERVER_CONNECTION_STATUS',
  payload,
});

export const updateBrowserCFDConnectionStatus: ActionCreator<HUB_ACTIONS, ConnectionStatus> = (
  payload
) => ({
  type: 'HUB_UPDATE_BROWSER_CFD_CONNECTION_STATUS',
  payload,
});

export const updateVersion: ActionCreator<HUB_ACTIONS, string> = (payload) => ({
  type: 'HUB_UPDATE_VERSION',
  payload,
});

export const updateDevices: ActionCreator<
  HUB_ACTIONS,
  {
    version?: string;
    defaultPrinters?: HubState['defaultPrinters'];
    selectedDisplay?: HubState['selectedDisplay'];
  }
> = (payload) => ({
  type: 'HUB_UPDATE_DEVICES',
  payload,
});

export const clearDevices = (): Action<HUB_ACTIONS> => ({
  type: 'HUB_CLEAR_DEVICES',
});
export const clearHubStore = (): Action<HUB_ACTIONS> => ({
  type: 'HUB_CLEAR_STORE',
});

export const updateDefaultPrinters: ActionCreator<
  HUB_ACTIONS,
  Partial<HubState['defaultPrinters']>
> = (payload) => ({
  type: 'HUB_UPDATE_DEFAULT_PRINTERS',
  payload,
});

function handleConnectionChange(state: WritableDraft<HubState>) {
  if (isStatusConnected(state.connection.local)) {
    state.connection.hub = 'Connected';
  }

  if (isStatusConnected(state.connection.local) && !isStatusConnected(state.connection.ably)) {
    state.connection.browserCFD = 'Disconnected';
  }
  if (!isStatusConnected(state.connection.ably) && !isStatusConnected(state.connection.local)) {
    state.connection.hub = 'Disconnected';
    state.connection.browserCFD = 'Disconnected';
    state.defaultPrinters = initialState.defaultPrinters;
    state.version = initialState.version;
    state.selectedDisplay = initialState.selectedDisplay;
  }
}

const createHubReducer = (): Reducer<HubState, Action<HUB_ACTIONS> & { payload: any }> =>
  createReducer(getHubSmartState() || initialState, {
    ABLY_UPDATE_CONNECTION_STATUS: (state, action) => {
      state.connection.ably = action.payload;
      handleConnectionChange(state);
    },
    HUB_LOCAL_SERVER_CONNECTION_STATUS: (state, action) => {
      state.connection.local = action.payload;
      handleConnectionChange(state);
    },
    HUB_UPDATE_DEFAULT_PRINTERS: (state, action) => {
      return {
        ...state,
        defaultPrinters: { ...state.defaultPrinters, ...action.payload },
      };
    },
    HUB_UPDATE_CONNECTION_STATUS: (state, action) => {
      state.connection.hub = action.payload;
      handleConnectionChange(state);
    },
    HUB_UPDATE_BROWSER_CFD_CONNECTION_STATUS: (state, action) => {
      state.connection.browserCFD = action.payload;
      handleConnectionChange(state);
    },
    HUB_CLEAR_DEVICES: (state) => {
      return {
        ...state,
        defaultPrinters: {},
        selectedDisplay: {},
        version: null,
      };
    },
    HUB_UPDATE_VERSION: (state, action) => {
      return {
        ...state,
        version: action.payload,
      };
    },
    HUB_UPDATE_DEVICES: (state, action) => {
      return {
        ...state,
        ...action.payload,
      };
    },
    HUB_UPDATE_CFD_MODAL: (state, action) => {
      return {
        ...state,
        ...action.payload,
      };
    },
    HUB_UPDATE_DOWNLOAD_STATUS: (state, action) => {
      return {
        ...state,
        ...action.payload,
      };
    },
    HUB_PRINT_TIMEOUT: (state, action) => {
      return {
        ...state,
        ...action.payload,
      };
    },
    HUB_CLEAR_STORE: () => {
      return initialState;
    },
  });

export default createHubReducer;
